import React from "react";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";

export const inputCls =
  "w-full rounded-md border border-line bg-bg px-3 py-2.5 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-brand-amber/60 transition-colors";

export const Field = ({ label, required, hint, children, testid }) => (
  <div data-testid={testid}>
    <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1.5">
      {label}{required && <span className="text-brand-amber ml-0.5">*</span>}
    </div>
    {children}
    {hint && <p className="text-[11px] text-ink-muted mt-1">{hint}</p>}
  </div>
);

export const TInput = (props) => <input {...props} className={inputCls} />;

export const TArea = (props) => <textarea {...props} className={`${inputCls} min-h-[90px] resize-y`} />;

export const SelectNative = ({ options, value, onChange, placeholder, testid }) => (
  <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={inputCls}>
    {placeholder && <option value="">{placeholder}</option>}
    {options.map((o) => {
      const v = typeof o === "string" ? o : o.v;
      const l = typeof o === "string" ? o : o.l;
      return <option key={v} value={v}>{l}</option>;
    })}
  </select>
);

export const Chips = ({ options, values = [], onChange, single = false, testidPrefix = "chip" }) => {
  const toggle = (v) => {
    if (single) { onChange(values[0] === v ? [] : [v]); return; }
    onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);
  };
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const v = typeof o === "string" ? o : o.v;
        const l = typeof o === "string" ? o : o.l;
        const on = values.includes(v);
        return (
          <button key={v} type="button" onClick={() => toggle(v)}
            data-testid={`${testidPrefix}-${v.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`rounded-full border px-3 py-1.5 text-xs transition ${
              on ? "border-brand-amber/70 bg-brand-amber/15 text-brand-amber"
                 : "border-line bg-bg-elevated text-ink-secondary hover:text-ink-primary hover:border-ink-muted"}`}>
            {l}
          </button>
        );
      })}
    </div>
  );
};

export const YesNo = ({ label, value, onChange, testid }) => (
  <div className="flex items-center justify-between gap-3 rounded-md border border-line bg-bg-elevated/50 px-3 py-2.5" data-testid={testid}>
    <span className="text-xs text-ink-primary">{label}</span>
    <div className="flex gap-1">
      {[["Yes", true], ["No", false]].map(([l, v]) => (
        <button key={l} type="button" onClick={() => onChange(v)}
          className={`rounded-md px-3 py-1 text-[11px] font-semibold transition ${
            value === v ? "bg-brand-amber text-black" : "bg-bg text-ink-muted hover:text-ink-primary border border-line"}`}>
          {l}
        </button>
      ))}
    </div>
  </div>
);

export const StepShell = ({
  step, title, subtitle, children, onBack, onContinue, onSkip,
  continueLabel = "Continue", skipLabel = "Skip this step", busy, canContinue = true,
}) => (
  <div className="w-full">
    <div className="mb-6">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-amber mb-2">
        // Step {String(step).padStart(2, "0")} of 11
      </div>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-ink-primary">{title}</h1>
      {subtitle && <p className="text-sm text-ink-secondary mt-2 max-w-xl">{subtitle}</p>}
    </div>
    <div className="space-y-5">{children}</div>
    <div className="mt-8 pt-5 border-t border-line flex flex-wrap items-center justify-between gap-3">
      <div>
        {onBack && (
          <button type="button" onClick={onBack} data-testid="wizard-back-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-4 py-2.5 text-sm text-ink-secondary hover:text-ink-primary transition">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
        )}
      </div>
      <div className="flex items-center gap-3">
        {onSkip && (
          <button type="button" onClick={onSkip} disabled={busy} data-testid="wizard-skip-btn"
            className="text-sm text-ink-muted hover:text-ink-primary transition disabled:opacity-50">
            {skipLabel}
          </button>
        )}
        {onContinue && (
          <button type="button" onClick={onContinue} disabled={busy || !canContinue}
            data-testid="wizard-continue-btn"
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-5 py-2.5 text-sm font-semibold text-black hover:bg-brand-amber/90 transition disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {continueLabel} <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  </div>
);
